import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Send, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { AccountContact } from '@/api/commercial/accounts';
import { type ProposalSendEmailResult, proposalApi } from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import {
  hasContractEnabledContact,
  hasContractRequiringItems,
} from '@/hooks/commercial/use-resume-validation';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import type { IProposalCartItem } from '@/types/commercial/cart';

type SubmitModalVariant = 'normal' | 'warning' | 'error';

interface ResumeSubmitDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
  clientName?: string;
  cartItems: IProposalCartItem[];
  contacts: AccountContact[];
  mode?: 'submit' | 'resend';
  onSuccess?: () => void;
}

function resolveSubmitVariant(
  cartItems: IProposalCartItem[],
  contacts: AccountContact[],
): SubmitModalVariant {
  if (!hasContractRequiringItems(cartItems)) {
    return 'normal';
  }
  return hasContractEnabledContact(contacts) ? 'warning' : 'error';
}

export function ResumeSubmitDialog({
  open,
  onClose,
  proposalId,
  clientName,
  cartItems,
  contacts,
  mode = 'submit',
  onSuccess,
}: ResumeSubmitDialogProps) {
  const queryClient = useQueryClient();
  const { formError, showFormError, clearFormError } = useFormError(open);
  const navigate = useNavigate();

  const variant = resolveSubmitVariant(cartItems, contacts);
  const isResend = mode === 'resend';

  const submitMutation = useMutation({
    retry: false,
    mutationFn: (): Promise<{ status: string } | ProposalSendEmailResult> => {
      if (!proposalId) throw new Error('Proposal ID is required');
      return isResend ? proposalApi.resend(proposalId) : proposalApi.submit(proposalId);
    },
    onSuccess: async (data) => {
      clearFormError();
      if ('sentTo' in data) {
        toast({
          variant: 'success',
          title: isResend ? 'Proposta reenviada' : 'Proposta enviada',
          description: `E-mail enviado para: ${data.sentTo}`,
        });
      } else {
        const statusLabel =
          data.status === 'PENDING_ACCEPT'
            ? 'Aguardando aceite'
            : data.status === 'PENDING_CREDIT'
              ? 'Aguardando análise de crédito'
              : data.status;

        toast({
          variant: 'success',
          title: 'Proposta enviada',
          description: `Status atualizado para: ${statusLabel}`,
        });
      }

      if (proposalId) {
        // Refresh inactive lists too, before leaving the editor. Keep the server
        // response as the source of truth, including PENDING_CREDIT submissions.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] }),
          queryClient.invalidateQueries({
            queryKey: ['proposal-credit-analysis-resolution', proposalId],
          }),
          queryClient.invalidateQueries({ queryKey: ['proposals'], refetchType: 'all' }),
        ]);
      }

      onClose();
      if (isResend) onSuccess?.();
      else navigate('/commercial/proposal', { replace: true });
    },
    onError: (error) => {
      showFormError(error);
      toast({
        title: isResend ? 'Erro ao reenviar proposta' : 'Erro ao enviar proposta',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  const actionLabel = isResend ? 'Reenviar' : 'Confirmar envio';
  const loadingLabel = isResend ? 'Reenviando...' : 'Enviando...';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitMutation.isPending && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isResend ? 'Reenviar Proposta' : 'Enviar Proposta'}</DialogTitle>
          <DialogDescription>
            {isResend
              ? 'Confirme o reenvio da proposta para o cliente.'
              : 'Confirme o envio da proposta para o cliente.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {variant === 'warning' && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <p className="text-sm text-amber-800">
                Atenção: esta proposta possui contratos pendentes de assinatura.
              </p>
            </div>
          )}

          {variant === 'error' && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <p className="text-sm text-red-800">
                É obrigatório selecionar um contato do tipo Contrato no step Cliente antes de enviar
                esta proposta.
              </p>
            </div>
          )}

          {clientName && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">Cliente:</span> {clientName}
            </p>
          )}

          {variant !== 'error' && (
            <p className="text-sm text-gray-600">
              Ao confirmar, a proposta será enviada e o status será atualizado para{' '}
              <span className="font-medium">&quot;Aguardando aceite&quot;</span>.
            </p>
          )}
        </div>

        <FormError error={formError} onDismiss={clearFormError} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitMutation.isPending}>
            {variant === 'error' ? 'Voltar' : 'Cancelar'}
          </Button>
          {variant !== 'error' && (
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-1 h-4 w-4" />
              )}
              {submitMutation.isPending ? loadingLabel : actionLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
